import { useState } from 'react';
import { Download, TrendingUp, AlertCircle, CheckCircle, XCircle, Code, Eye, Maximize2 } from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, RadialLinearScale } from 'chart.js';
import { Pie, Radar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  RadialLinearScale
);

const CompetitiveAnalysisReport = ({ analysisData, appOptions, onDownload }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('rendered'); // 'rendered' or 'code'
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Generate downloadable HTML report
  const generateHTMLReport = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Competitive Analysis Report - ${appOptions.yourProduct || 'Your Product'}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            background: #f5f5f5;
        }
        .report-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 40px;
            border-radius: 10px;
            margin-bottom: 30px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .report-header h1 {
            margin: 0 0 10px 0;
            font-size: 2.5em;
        }
        .report-header p {
            margin: 0;
            opacity: 0.9;
        }
        .section {
            background: white;
            padding: 30px;
            margin-bottom: 20px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        .section h2 {
            color: #667eea;
            margin-top: 0;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 10px;
        }
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin: 20px 0;
        }
        .metric-card {
            background: #f7fafc;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
        }
        .metric-card h3 {
            margin: 0 0 10px 0;
            color: #4a5568;
            font-size: 0.9em;
            text-transform: uppercase;
        }
        .metric-value {
            font-size: 2em;
            font-weight: bold;
            color: #667eea;
        }
        .comparison-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
        }
        .comparison-table th {
            background: #667eea;
            color: white;
            padding: 12px;
            text-align: left;
        }
        .comparison-table td {
            padding: 12px;
            border-bottom: 1px solid #e2e8f0;
        }
        .comparison-table tr:hover {
            background: #f7fafc;
        }
        .feature-check {
            color: #48bb78;
            font-weight: bold;
        }
        .feature-x {
            color: #f56565;
            font-weight: bold;
        }
        .swot-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            margin: 20px 0;
        }
        .swot-box {
            padding: 20px;
            border-radius: 8px;
        }
        .swot-box.strengths {
            background: #c6f6d5;
            border: 1px solid #9ae6b4;
        }
        .swot-box.weaknesses {
            background: #fed7d7;
            border: 1px solid #feb2b2;
        }
        .swot-box.opportunities {
            background: #bee3f8;
            border: 1px solid #90cdf4;
        }
        .swot-box.threats {
            background: #feebc8;
            border: 1px solid #fbd38d;
        }
        .recommendation {
            background: #e6fffa;
            border: 1px solid #81e6d9;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
        }
        .chart-container {
            position: relative;
            height: 400px;
            margin: 20px 0;
        }
        @media print {
            body {
                background: white;
            }
            .section {
                box-shadow: none;
                border: 1px solid #e2e8f0;
            }
        }
    </style>
</head>
<body>
    <div class="report-header">
        <h1>Competitive Analysis Report</h1>
        <p>Product: ${appOptions.yourProduct || 'Not specified'}</p>
        <p>Analysis Date: ${new Date().toLocaleDateString()}</p>
        <p>Competitors Analyzed: ${appOptions.competitors ? appOptions.competitors.split('\n').length : 0}</p>
    </div>

    ${analysisData ? `
    <div class="section">
        <h2>Executive Summary</h2>
        <p>${analysisData.executiveSummary || 'Analysis in progress...'}</p>
    </div>

    <div class="section">
        <h2>Market Overview</h2>
        <div class="metrics-grid">
            <div class="metric-card">
                <h3>Market Size</h3>
                <div class="metric-value">${analysisData.marketSize || 'N/A'}</div>
            </div>
            <div class="metric-card">
                <h3>Growth Rate</h3>
                <div class="metric-value">${analysisData.growthRate || 'N/A'}</div>
            </div>
            <div class="metric-card">
                <h3>Total Competitors</h3>
                <div class="metric-value">${analysisData.totalCompetitors || appOptions.competitors?.split('\n').length || 0}</div>
            </div>
        </div>
    </div>

    <div class="section">
        <h2>Feature Comparison</h2>
        <table class="comparison-table">
            <thead>
                <tr>
                    <th>Feature</th>
                    <th>${appOptions.yourProduct || 'Your Product'}</th>
                    ${analysisData.competitors?.map(comp => `<th>${comp.name}</th>`).join('') || ''}
                </tr>
            </thead>
            <tbody>
                ${analysisData.features?.map(feature => `
                    <tr>
                        <td>${feature.name}</td>
                        <td>${feature.yourProduct ? '<span class="feature-check">✓</span>' : '<span class="feature-x">✗</span>'}</td>
                        ${feature.competitors?.map(has => has ? '<td><span class="feature-check">✓</span></td>' : '<td><span class="feature-x">✗</span></td>').join('') || ''}
                    </tr>
                `).join('') || '<tr><td colspan="3">No feature data available</td></tr>'}
            </tbody>
        </table>
    </div>

    <div class="section">
        <h2>SWOT Analysis</h2>
        <div class="swot-grid">
            <div class="swot-box strengths">
                <h3>Strengths</h3>
                <ul>
                    ${analysisData.swot?.strengths?.map(item => `<li>${item}</li>`).join('') || '<li>Analysis pending</li>'}
                </ul>
            </div>
            <div class="swot-box weaknesses">
                <h3>Weaknesses</h3>
                <ul>
                    ${analysisData.swot?.weaknesses?.map(item => `<li>${item}</li>`).join('') || '<li>Analysis pending</li>'}
                </ul>
            </div>
            <div class="swot-box opportunities">
                <h3>Opportunities</h3>
                <ul>
                    ${analysisData.swot?.opportunities?.map(item => `<li>${item}</li>`).join('') || '<li>Analysis pending</li>'}
                </ul>
            </div>
            <div class="swot-box threats">
                <h3>Threats</h3>
                <ul>
                    ${analysisData.swot?.threats?.map(item => `<li>${item}</li>`).join('') || '<li>Analysis pending</li>'}
                </ul>
            </div>
        </div>
    </div>

    <div class="section">
        <h2>Key Recommendations</h2>
        ${analysisData.recommendations?.map(rec => `
            <div class="recommendation">
                <h3>${rec.title}</h3>
                <p>${rec.description}</p>
            </div>
        `).join('') || '<p>Recommendations will be generated after analysis.</p>'}
    </div>
    ` : '<div class="section"><p>Analysis data is being generated...</p></div>'}

    <div class="section">
        <p style="text-align: center; color: #718096; font-size: 0.9em;">
            Generated by Supple AI Competitive Analysis Tool<br>
            Report Date: ${new Date().toLocaleString()}
        </p>
    </div>
</body>
</html>
    `;
    
    return htmlContent;
  };

  const handleDownload = () => {
    const htmlContent = generateHTMLReport();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `competitive-analysis-${appOptions.yourProduct?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'report'}-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    if (onDownload) {
      onDownload();
    }
  };

  // Sample chart data (this would come from actual analysis)
  const marketShareData = {
    labels: [appOptions.yourProduct || 'Your Product', ...(analysisData?.competitors?.map(c => c.name) || ['Competitor 1', 'Competitor 2'])],
    datasets: [{
      data: analysisData?.marketShare || [30, 45, 25],
      backgroundColor: ['#667eea', '#f56565', '#48bb78', '#ed8936', '#38b2ac'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const featureComparisonData = {
    labels: analysisData?.featureCategories || ['Core Features', 'Advanced Features', 'Integration', 'Support', 'Pricing'],
    datasets: [
      {
        label: appOptions.yourProduct || 'Your Product',
        data: analysisData?.yourProductScores || [4, 3, 5, 4, 3],
        backgroundColor: 'rgba(102, 126, 234, 0.5)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2
      },
      ...(analysisData?.competitors?.map((comp, idx) => ({
        label: comp.name,
        data: comp.scores || [3, 4, 3, 5, 4],
        backgroundColor: `rgba(${idx * 50 + 100}, ${idx * 30 + 50}, ${idx * 40 + 100}, 0.5)`,
        borderColor: `rgba(${idx * 50 + 100}, ${idx * 30 + 50}, ${idx * 40 + 100}, 1)`,
        borderWidth: 2
      })) || [])
    ]
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-600" />
            Competitive Analysis Report
          </h2>
          <p className="text-gray-600 mt-1">
            Comprehensive analysis of {appOptions.yourProduct || 'your product'} vs competitors
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Switcher */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('rendered')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'rendered'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Eye className="w-4 h-4" />
              Rendered
            </button>
            <button
              onClick={() => setViewMode('code')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'code'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Code className="w-4 h-4" />
              Code
            </button>
          </div>
          
          {/* Fullscreen button (only in rendered mode) */}
          {viewMode === 'rendered' && (
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
              title="View in fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download Report
          </button>
        </div>
      </div>

      {/* Conditional Content based on view mode */}
      {viewMode === 'code' ? (
        /* Code View */
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">HTML Report Code</h3>
            <button
              onClick={() => {
                const htmlContent = generateHTMLReport();
                navigator.clipboard.writeText(htmlContent);
              }}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Copy to Clipboard
            </button>
          </div>
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-[600px] text-sm font-mono">
            <code>{generateHTMLReport()}</code>
          </pre>
        </div>
      ) : (
        /* Rendered View */
        <div className={isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : ''}>
          {isFullscreen && (
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Competitive Analysis Report - Fullscreen</h3>
              <button
                onClick={() => setIsFullscreen(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          )}
          
          <div className={isFullscreen ? 'p-6' : ''}>
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex gap-6">
                {['overview', 'features', 'market', 'swot', 'recommendations'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </nav>
            </div>

      {/* Content based on active tab */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-purple-600 mb-1">Product Analyzed</h3>
                <p className="text-2xl font-bold text-purple-900">{appOptions.yourProduct || 'Not specified'}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-600 mb-1">Competitors</h3>
                <p className="text-2xl font-bold text-blue-900">{appOptions.competitors?.split('\n').length || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-600 mb-1">Keywords</h3>
                <p className="text-2xl font-bold text-green-900">{appOptions.keywords?.split(',').length || 0}</p>
              </div>
            </div>
            
            {analysisData?.executiveSummary && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">Executive Summary</h3>
                <p className="text-gray-700 leading-relaxed">{analysisData.executiveSummary}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Feature Comparison Matrix</h3>
            {analysisData?.features ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left">Feature</th>
                      <th className="border p-3 text-center">{appOptions.yourProduct || 'Your Product'}</th>
                      {analysisData.competitors?.map((comp, idx) => (
                        <th key={idx} className="border p-3 text-center">{comp.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analysisData.features.map((feature, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="border p-3 font-medium">{feature.name}</td>
                        <td className="border p-3 text-center">
                          {feature.yourProduct ? 
                            <CheckCircle className="w-5 h-5 text-green-600 mx-auto" /> : 
                            <XCircle className="w-5 h-5 text-red-600 mx-auto" />
                          }
                        </td>
                        {feature.competitors?.map((has, compIdx) => (
                          <td key={compIdx} className="border p-3 text-center">
                            {has ? 
                              <CheckCircle className="w-5 h-5 text-green-600 mx-auto" /> : 
                              <XCircle className="w-5 h-5 text-red-600 mx-auto" />
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3" />
                <p>Feature analysis data will appear here after processing</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'market' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="text-lg font-semibold mb-4">Market Share Distribution</h3>
                <div className="h-64">
                  <Pie data={marketShareData} options={{ maintainAspectRatio: false }} />
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="text-lg font-semibold mb-4">Competitive Positioning</h3>
                <div className="h-64">
                  <Radar data={featureComparisonData} options={{ maintainAspectRatio: false }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'swot' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800 mb-3">Strengths</h3>
              <ul className="space-y-2">
                {analysisData?.swot?.strengths?.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )) || <li className="text-gray-600">Analysis pending...</li>}
              </ul>
            </div>
            <div className="bg-red-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-red-800 mb-3">Weaknesses</h3>
              <ul className="space-y-2">
                {analysisData?.swot?.weaknesses?.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )) || <li className="text-gray-600">Analysis pending...</li>}
              </ul>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">Opportunities</h3>
              <ul className="space-y-2">
                {analysisData?.swot?.opportunities?.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )) || <li className="text-gray-600">Analysis pending...</li>}
              </ul>
            </div>
            <div className="bg-yellow-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Threats</h3>
              <ul className="space-y-2">
                {analysisData?.swot?.threats?.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                )) || <li className="text-gray-600">Analysis pending...</li>}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {analysisData?.recommendations?.map((rec, idx) => (
              <div key={idx} className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">{rec.title}</h3>
                <p className="text-gray-700 mb-3">{rec.description}</p>
                <div className="flex items-center gap-2 text-sm text-purple-600">
                  <span className="font-medium">Priority:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                    rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {rec.priority?.toUpperCase() || 'MEDIUM'}
                  </span>
                </div>
              </div>
            )) || (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3" />
                <p>Strategic recommendations will be generated after analysis</p>
              </div>
            )}
          </div>
        )}
      </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitiveAnalysisReport;